import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultResponse, FrontendMandant, GlobalFrontendUser, MyMandantsResponse, PortalJwtPayload, User } from '@reflact/reflactportal';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { LogtoService } from './logto.service';
@Injectable({
  providedIn: 'root'
})
export class LoginService {

  public loggedInUser$ = new BehaviorSubject<GlobalFrontendUser | undefined>(undefined);
  public loggedInUser: GlobalFrontendUser | undefined = undefined;
  public loggedInMandantUser: User | null = null;
  public selectedMandant$ = new BehaviorSubject<FrontendMandant | null>(null)

  public token: string | undefined = undefined


  constructor(private http: HttpClient, private logtoService: LogtoService) {
    const token = localStorage.getItem('authToken');
    if (token != null) {
      this.token = token;
    }
  }
  public getApplicationLanguage() {
    return (window.location.href.indexOf("/portal/de") >= 0) ? "de" : "en"
  }

  public getTokenAndLogin(): DefaultResponse & Partial<PortalJwtPayload> {
    const token = localStorage.getItem('authToken');
    if (token == null) {
      return { status: 'forbidden' };
    }
    this.token = token
    const decoded = jwtDecode<PortalJwtPayload & JwtPayload>(token);
    if (decoded.globalUser == null || (decoded.exp != null && new Date(decoded.exp * 1000).getTime() <= new Date().getTime())) {
      localStorage.removeItem("authToken")
      return { status: 'forbidden' };
    }
    if (decoded.globalUser) {
      this.selectedMandant$.next(decoded.mandant)
      this.loggedInUser = decoded.globalUser
      this.loggedInUser$.next(decoded.globalUser)
      this.loggedInMandantUser = decoded.mandantUser;
    }
    return { status: 'ok', ...decoded }
  }

  /** Re-fetches a token via Logto for the currently selected mandant (or a plain
   * non-org-scoped token if none is selected) instead of the removed self-signed
   * `/api/auth/refreshtoken` — e.g. after editing your own reflactadmin flag. */
  public async refreshToken(): Promise<DefaultResponse> {
    try {
      const mandantId = this.selectedMandant$.value?._id?.toString();
      const token = mandantId ? await this.logtoService.getOrganizationToken(mandantId) : await this.logtoService.getAccessToken();
      await this.loginWithToken(token);
      return { status: 'ok' };
    } catch {
      return { status: 'error' };
    }
  }

  public async loginWithToken(token: string): Promise<PortalJwtPayload | undefined> {
    if (!token) return undefined;
    const payload: PortalJwtPayload = jwtDecode(token);
    this.token = token;
    localStorage.setItem("authToken", token);
    if (payload.globalUser) {
      this.loggedInUser = payload.globalUser;
      this.loggedInUser$.next(payload.globalUser)
      this.selectedMandant$.next(payload.mandant)
    }
    return payload;
  }

  public getAuthHeaders(): any {
    return {
      token: localStorage.getItem('authToken')
    };
  }

  public getAuthorizationToken(): string | undefined {
    if (this.token == undefined) {
      return undefined;
    }
    return 'Bearer ' + this.token;
  }

  public async getMandantsForUser(): Promise<MyMandantsResponse> {
    const res = await firstValueFrom(this.http.get<MyMandantsResponse>("/api/user/myMandants"));
    return res;
  }

  public async refreshAccount(): Promise<void> {
    await this.logtoService.clearAccessToken();
    if ((await this.refreshToken()).status !== 'ok') {
      throw new Error('Die Kontodaten konnten nicht aktualisiert werden. Bitte erneut anmelden.');
    }
    this.getTokenAndLogin();
  }

  public async selectAfterLeaving(mandantId: string): Promise<void> {
    this.token = undefined;
    localStorage.removeItem('authToken');
    this.selectedMandant$.next(null);
    this.loggedInMandantUser = null;
    await this.logtoService.clearAccessToken();
    if ((await this.selectMandant(mandantId)).status !== 'ok') {
      throw new Error('Mandant verlassen. Bitte erneut anmelden.');
    }
    this.getTokenAndLogin();
  }

  /** Switches to a different mandant by requesting an organization-scoped Logto token for it. */
  public async selectMandant(mandantId: string): Promise<DefaultResponse> {
    try {
      const token = await this.logtoService.getOrganizationToken(mandantId);
      await this.loginWithToken(token);
      return { status: 'ok' };
    } catch {
      return { status: 'error' };
    }
  }


}

